import React from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { MinusIcon, PlusIcon } from "@plex/icons";
import { SelectionMode, DataTableDef, Form } from "@plex/react-components";

import { InlineEdit } from "ui/components";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers";

import { releaseMessages } from "../../release.localization";
import { releaseDetailsManagerActions } from "../ReleaseDetails.actions";
import styles from "../ReleaseDetails.module.scss";
import { IReleaseDetailsState, IMessageData, Status } from "../ReleaseDetails.state";

export interface IReleaseMessagesGridProps {
  associatedMessages: IMessageData[];
  selectedMessages: IMessageData[];
  status: string;
  selectExistingRelationship: (messageId: IMessageData[]) => void;
}

const ReleaseMessagesGrid: React.FC<IReleaseMessagesGridProps> = ({ associatedMessages, selectedMessages, status, selectExistingRelationship }) => {
  const localizedValues = useLocalizedValues(() => ({
    messageIdTitle: displayMessages.property("messageId"),
    textTitle: displayMessages.property("text"),
    status: displayMessages.property("status")
  }));

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexTechnicalWriter || status === releaseMessages.option("releaseStatusPlanning").value;

  const renderStatus = (status: Status) => {
    switch (status) {
      case Status.Added:
        return (
          <div className={styles.iconContainer}>
            <PlusIcon className={styles.plusIcon} />
          </div>
        );
      case Status.Deleted:
        return (
          <div className={styles.iconContainer}>
            <MinusIcon className={styles.minusIcon} />
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <Form.Row>
      <DataTableDef<IMessageData>
        selected={selectedMessages}
        keySelector={row => row.message.messageId}
        selectionMode={SelectionMode.multiple}
        data={associatedMessages}
        onSelectionChanged={selectExistingRelationship}
        className={styles.base}
        canSelectRow={() => isReadOnlyMode}
      >
        <DataTableDef.Column<IMessageData> id="messageId" key="1" sortable shortTitle={localizedValues.messageIdTitle} valueSelector={row => row.message.messageId}>
          {row => (
            <InlineEdit<IMessageData>
              initialValue={row.message.messageId}
              originalValue={row.message.messageId}
              className={styles.inlineEdit}
              context={row}
              canEdit={() => false}
              onValueChanged={() => {}}
              validationIssues={row.validationIssues}
            >
              {row.message.messageId}
            </InlineEdit>
          )}
        </DataTableDef.Column>
        <DataTableDef.Column<IMessageData> id="text" key="2" sortable shortTitle={localizedValues.textTitle} valueSelector={row => row.message.text}>
          {row => row.message.text}
        </DataTableDef.Column>
        <DataTableDef.Column<IMessageData> id="status" key="3" sortable shortTitle={localizedValues.status} valueSelector={row => row.status} className={styles.columnStatus}>
          {row => renderStatus(row.status)}
        </DataTableDef.Column>
      </DataTableDef>
    </Form.Row>
  );
};

export default connect(
  (state: { releaseDetails: IReleaseDetailsState }) => {
    return {
      associatedMessages: state.releaseDetails.associatedMessages,
      selectedMessages: state.releaseDetails.selectedAssociatedMessages,
      status: state.releaseDetails.releaseStatus
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectExistingRelationship: releaseDetailsManagerActions.selectExistingMessage
      },
      dispatch
    )
)(ReleaseMessagesGrid);
